import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
    Attachment,
    CloseIcon,
    FilesPdfIcon,
    FilesPictureColoredIcon,
    FilesUploadIcon,
    Flex,
} from "@fluentui/react-northstar";

import {
    acceptStyle,
    baseStyle,
    focusedStyle,
    IconUpload,
    rejectStyle,
    TextStyle,
    TextUpload,
    Wrapper,
} from "./index.style";
import { BASE_URL_API } from "../../../../constants";
import { formatFileSize } from "../../../../utils/functions";

// Fonction pour déterminer l'icône basée sur l'extension
const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    if (extension === "pdf") return <FilesPdfIcon />;
    if (extension === "jpg" || extension === "jpeg")
        return <FilesPictureColoredIcon />;
    return null; // Icône par défaut pour les extensions inconnues
};

const DropzoneDocuments = ({
    onSuccessUploadFile = () => null,
    default_files = [],
}) => {
    const [files, setFiles] = useState([]); // Tous les fichiers (anciens + nouveaux)
    const [progress, setProgress] = useState({}); // Progress for each file

    const removeFile = (file) => {
        onSuccessUploadFile((prev) => {
            return prev.filter((prev) => prev.original_filename !== file.name);
        });
        return setFiles((prevFiles) => {
            return prevFiles.filter((prevFile) => prevFile.name !== file.name);
        });
    };

    const onDrop = useCallback((acceptedFiles) => {
        const maxFileSize = 2.5 * 1024 * 1024; // 2.5 Mo en octets
        const validFiles = [];
        const invalidFiles = [];

        acceptedFiles.forEach((file) => {
            if (file.size <= maxFileSize) {
                validFiles.push(file);
            } else {
                invalidFiles.push(file.name); // Collecter les noms des fichiers trop volumineux
            }
        });

        if (invalidFiles?.length > 0) {
            console.log(
                `Les fichiers suivants dépassent la taille maximale de 2,5 Mo : ${invalidFiles.join(
                    ", "
                )}`
            );
        }

        // Ajouter les fichiers valides à la liste existante
        setFiles((prevFiles) => [...prevFiles, ...validFiles]);

        // Initialiser la progression pour les nouveaux fichiers valides
        const initialProgress = {};
        validFiles.forEach((file) => {
            initialProgress[file.name] = 0;
        });
        setProgress((prevProgress) => ({
            ...prevProgress,
            ...initialProgress,
        }));
        // uploadFiles();
    }, []);

    const onDropRejected = useCallback((fileRejections) => {
        // Afficher un message d'erreur pour les fichiers rejetés
        const invalidFiles = fileRejections
            .map((rejection) => rejection.file.name)
            .join(", ");
        alert(
            `Les fichiers suivants ont des extensions non valides : ${invalidFiles}`
        );
    }, []);

    useEffect(() => {
        if (files?.length > 0) {
            uploadFiles();
        }
    }, [files?.length]);

    useEffect(() => {
        if (default_files?.length > 0) {
            setFiles(default_files);
        }
    }, []);

    const uploadFiles = () => {
        const xhr = new XMLHttpRequest();
        xhr.withCredentials = true;
        const formData = new FormData();

        for (let i = 0; i < files?.length; i++) {
            const current_file = files[i];
            if (!current_file?.is_default) {
                formData.append("uploaded_files", files[i]);
            }
        }

        // ne pas envoyer un FormData Empty.
        let isEmpty = true;
        for (const pair of formData.entries()) {
            isEmpty = false;
            break;
        }
        if (isEmpty) {
            // ne pas envoyer un FormData Empty.
            return;
        }

        xhr.open("POST", `${BASE_URL_API}/upload_file/`, true);

        // Track upload progress
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                let uploadedSize = event.loaded;

                const updatedProgress = {};
                files
                    .filter((file) => !file?.is_default)
                    .forEach((file) => {
                        const fileSize = file.size;
                        const fileProgress = Math.min(
                            1,
                            uploadedSize / fileSize
                        );
                        updatedProgress[file.name] = fileProgress * 100; // Percentage
                        uploadedSize -= fileSize;
                    });

                setProgress((prev) => ({ ...prev, ...updatedProgress }));
            }
        };

        xhr.onload = () => {
            if (xhr.status === 200) {
                const response = JSON.parse(xhr.response);

                onSuccessUploadFile(response.files); // sauvgarder les fichiers dans un state
            } else {
                console.error("Upload failed:", xhr.statusText);
            }
        };

        xhr.onerror = () => {
            console.error("An error occurred during the upload.");
        };

        xhr.send(formData);
    };

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        accept: {
            "application/pdf": [".pdf"],
            "image/jpeg": [".jpg", ".jpeg"],
        },
        onDrop: onDrop,
        onDropRejected: onDropRejected,
        maxFiles: 5,
    });

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    return (
        <Flex fill column gap="gap.smaller">
            <Wrapper
                {...getRootProps({ style })}
                column
                gap="gap.small"
                fill
                hAlign="center"
                vAlign="center"
            >
                <Flex column gap="gap.medium" hAlign="center">
                    <input {...getInputProps()} />
                </Flex>
                <Flex hAlign="center" vAlign="center" column gap="gap.medium">
                    <IconUpload hAlign="center" vAlign="center">
                        <FilesUploadIcon
                            styles={{ color: "white" }}
                            size="small"
                        />
                    </IconUpload>
                    <Flex>
                        <TextUpload align="center">
                            <span>Cliquez pour importer</span> ou drag and drop
                        </TextUpload>
                    </Flex>
                </Flex>
            </Wrapper>
            <Flex column gap="gap.smaller" styles={{ width: "100%" }}>
                <TextStyle>
                    <b>Poids maximal du fichier :</b> 2,5 Mo
                </TextStyle>
                <TextStyle>
                    <b>Formats acceptés : </b>.jpg, .jpeg, .pdf
                </TextStyle>
            </Flex>
            {/* show files */}
            <Flex gap="gap.small" wrap styles={{ rowGap: "4px" }}>
                {files.map((file) => {
                    return (
                        <Attachment
                            key={file.name}
                            header={file.name}
                            actionable
                            icon={getFileIcon(file.name)}
                            action={{
                                icon: <CloseIcon />,
                                onClick: () => removeFile(file),
                                title: "Close",
                            }}
                            progress={
                                progress[file.name] === 100
                                    ? 0
                                    : progress[file.name]
                            }
                            description={formatFileSize(file.size)}
                            inverted
                            style={{
                                maxWidth: "335px",
                                backgroundColor: "white",
                            }}
                        />
                    );
                })}
            </Flex>
        </Flex>
    );
};

export default DropzoneDocuments;
